import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import madrasatiLightLogo from '../assets/madrasati-light.svg';

function EmailConfirmationPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        position: 'relative',
        backgroundColor: theme.palette.secondary.main,
        overflow: 'hidden',
        padding: { xs: 2, md: 4 },
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          width: { xs: '150px', sm: '250px', md: '300px' },
          height: { xs: '150px', sm: '250px', md: '300px' },
          borderRadius: '50%',
          background: `linear-gradient(135deg, rgba(52, 152, 219, 0.09) 0%, rgba(41, 128, 185, 0.15) 100%)`,
          top: { xs: '-50px', sm: '-75px', md: '-100px' },
          right: { xs: '-50px', sm: '-75px', md: '-100px' },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: { xs: '100px', sm: '150px', md: '200px' },
          height: { xs: '100px', sm: '150px', md: '200px' },
          borderRadius: '50%',
          background: `linear-gradient(135deg, rgba(41, 128, 185, 0.12) 0%, rgba(52, 152, 219, 0.18) 100%)`,
          bottom: { xs: '20px', sm: '35px', md: '50px' },
          left: { xs: '20px', sm: '35px', md: '50px' },
        }}
      />

      {/* Confirmation Card */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '550px',
          borderRadius: { xs: '16px', sm: '20px', md: '24px' },
          border: '2px solid rgba(255, 255, 255, 0.3)',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          padding: { xs: 2.5, sm: 3.5, md: 5 },
          zIndex: 1,
          textAlign: 'center',
        }}
      >
        {/* Logo at top */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          <Box
            component="img"
            src={madrasatiLightLogo}
            alt="Madrasati Logo"
            sx={{
              width: '100%',
              maxWidth: { xs: '140px', sm: '160px', md: '180px' },
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Success Icon */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: { xs: 60, sm: 70, md: 80 },
              color: theme.palette.success.main,
            }}
          />
        </Box>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: theme.typography.fontWeightBold,
            color: theme.palette.secondary.contrastText,
            mb: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
          }}
        >
          Email Confirmed!
        </Typography>

        {/* Pending Approval Section */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'rgba(255, 193, 7, 0.15)',
            border: '1px solid rgba(255, 193, 7, 0.4)',
            borderRadius: { xs: '12px', sm: '14px', md: '16px' },
            padding: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: { xs: 1.5, sm: 2 },
              flexWrap: 'wrap',
            }}
          >
            <HourglassIcon
              sx={{
                fontSize: { xs: 32, sm: 36, md: 40 },
                color: theme.palette.warning.main,
                mr: { xs: 0.75, sm: 1 },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.warning.dark,
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              }}
            >
              Pending Approval
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{
              color: theme.palette.secondary.contrastText,
              lineHeight: 1.7,
              fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
            }}
          >
            Your email has been successfully verified. Your account is now pending approval from an administrator.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: theme.palette.secondary.contrastText,
              opacity: 0.85,
              mt: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            }}
          >
            You will receive a notification once your account has been approved and you can log in.
          </Typography>
        </Paper>

        {/* What happens next */}
        <Box
          sx={{
            textAlign: 'left',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: { xs: '10px', sm: '12px' },
            padding: { xs: 2, sm: 2.25, md: 2.5 },
            mb: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.palette.secondary.contrastText,
              mb: { xs: 1.25, sm: 1.5 },
              fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
            }}
          >
            What happens next?
          </Typography>
          <Box component="ul" sx={{ margin: 0, paddingLeft: { xs: 2, sm: 2.25, md: 2.5 } }}>
            <Typography
              component="li"
              variant="body2"
              sx={{
                color: theme.palette.secondary.contrastText,
                opacity: 0.9,
                mb: { xs: 0.75, sm: 1 },
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              }}
            >
              An administrator will review your registration
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{
                color: theme.palette.secondary.contrastText,
                opacity: 0.9,
                mb: { xs: 0.75, sm: 1 },
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              }}
            >
              Once approved, you'll be able to log in with your credentials
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{
                color: theme.palette.secondary.contrastText,
                opacity: 0.9,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              }}
            >
              This process typically takes 1-2 business days
            </Typography>
          </Box>
        </Box>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => navigate('/login')}
          sx={{
            py: { xs: 1.25, sm: 1.375, md: 1.5 },
            borderRadius: { xs: '10px', sm: '12px' },
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
          }}
        >
          Go to Login
        </Button>
      </Box>
    </Box>
  );
}

export default EmailConfirmationPage;

